import React, { FC, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { CreateButton, Drawer } from '~/components';
import { CreateForm } from '~/components/ra';
import { history } from '~/App';
import onError from '~/errorsHandler';
import MenuForm from './MenuForm';
import ChildForm from './ChildForm';
import { queryClient } from '~/App';

const l = (key: string): string => `resources.menu.create.${key}`;

interface CreateDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedType: 'menu' | 'menuItem';
  menuId?: string;
  parentId?: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
}

const CreateDrawer: FC<CreateDrawerProps> = (props) => {
  const { open, onClose, selectedType, menuId, parentId, loadChildren } = props;

  const handleError = (error: any) => {
    onError(error);
  };
  const handleClose = () => {
    onClose();
  };

  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  if (!permissions?.includes('dynamicMenu.create')) return null;
  return (
    <Drawer
      open={open}
      onClose={handleClose}
      heading={selectedType === 'menuItem' ? t('addChild') : t('title')}
    >
      <CreateForm
        resource='menu'
        id={selectedType === 'menuItem' || selectedType === 'menu' ? menuId : undefined}
        subresource={selectedType === 'menuItem' ? 'items' : undefined}
        onSuccess={(res: any) => {
          if (selectedType === 'menuItem') {
            handleClose();
            if (parentId && loadChildren) {
              loadChildren?.(parentId, true);
            } else {
              queryClient.invalidateQueries({
                queryKey: ['menu', menuId, 'items', '$'],
              });
            }
          } else {
            history.push(`/menu/${res.data.id}/show`);
          }
        }}
        onError={handleError}
        disabled={!permissions?.includes('dynamicMenu.create')}
        parseRecordBeforeSubmit={(item) => {
          if (selectedType === 'menuItem' && parentId) {
            return {
              ...item,
              parentId,
            };
          } else {
            return item;
          }
        }}
      >
        {selectedType === 'menu' && <MenuForm />}
        {selectedType === 'menuItem' && <ChildForm menuId={menuId} />}
      </CreateForm>
    </Drawer>
  );
};

interface CreateActionProps {
  selectedType: 'menu' | 'menuItem';
  menuId?: string;
  parentId?: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
}

const CreateAction: FC<CreateActionProps> = (props) => {
  const { selectedType, menuId, parentId, loadChildren } = props;
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const { permissions } = usePermissions();
  if (!permissions?.includes('dynamicMenu.create')) return null;
  return (
    <>
      <CreateButton
        onClick={handleOpen}
        label={selectedType === 'menuItem' ? t('addChild') : undefined}
      />
      <CreateDrawer
        open={open}
        onClose={handleClose}
        selectedType={selectedType}
        menuId={menuId}
        parentId={parentId}
        loadChildren={loadChildren}
      />
    </>
  );
};

export default CreateAction;
